import React, { useMemo } from "react";
import { resolveCategoryPick } from "./categoryPick";

/**
 * O mínimo que o seletor precisa de uma categoria. Estrutural de propósito: a tela interna
 * (`TicketCategory`) e a do portal (`ClientTicketCategory`) têm formas diferentes — a do cliente
 * omite fila e contagem —, e as duas satisfazem isto. Um tipo importado de um dos lados obrigaria
 * o outro a conhecer campos que ele nunca deveria receber.
 */
export interface CategoryNode {
  id: number;
  name: string;
  children?: CategoryNode[];
}

interface Props {
  categories: CategoryNode[];
  /** A **folha** escolhida — pode ser uma raiz, quando nenhuma subcategoria serve. */
  value: number | null;
  onChange: (categoryId: number | null) => void;
  disabled?: boolean;
  /** Texto sob os selects. Some quando não há nada a dizer. */
  hint?: string;
}

const selectCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-navy-900 dark:text-white";

/**
 * Dois selects encadeados: categoria → subcategoria.
 *
 * O valor que sai é **um id só** — a folha. Escolher só a categoria principal é uma resposta
 * completa ("é de Acesso, mas não sei qual"), e por isso o segundo select é opcional em vez de
 * obrigatório: exigir a subcategoria transformaria "não sei" em "escolha qualquer uma", que é
 * exatamente o dado que estraga o relatório.
 */
const TicketCategoryPicker: React.FC<Props> = ({ categories, value, onChange, disabled, hint }) => {
  // A raiz é DERIVADA do valor, nunca guardada em estado próprio (ver `categoryPick.ts`).
  const { rootId, subId } = useMemo(() => resolveCategoryPick(categories, value), [categories, value]);

  const filhas = useMemo(
    () => categories.find((c) => c.id === rootId)?.children ?? [],
    [categories, rootId]
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <select
          className={selectCls}
          disabled={disabled}
          aria-label="Categoria"
          value={rootId ?? ""}
          // Trocar a categoria principal zera a subcategoria: manter a antiga deixaria gravada uma
          // folha que não pertence mais à raiz escolhida.
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        >
          <option value="">— Sem categoria —</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          className={selectCls}
          aria-label="Subcategoria"
          disabled={disabled || rootId == null || filhas.length === 0}
          value={subId ?? ""}
          // Vazio devolve a RAIZ, e não `null`: limpar a subcategoria não pode desclassificar o
          // chamado inteiro — quem quer limpar tudo usa o primeiro select.
          onChange={(e) => onChange(e.target.value === "" ? rootId : Number(e.target.value))}
        >
          <option value="">
            {rootId == null
              ? "— Escolha a categoria —"
              : filhas.length === 0
              ? "— Sem subcategorias —"
              : "— Nenhuma específica —"}
          </option>
          {filhas.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </div>

      {hint && <p className="text-[11px] text-gray-500 dark:text-gray-400">{hint}</p>}
    </div>
  );
};

export default TicketCategoryPicker;
